import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Edit as EditIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchClientSettings, addClientSetting, updateClientSetting } from '../api/features/clientData/clientApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { styled } from '@mui/material/styles';

const SAPDataCard = styled(Card)(() => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(() => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));

const SAPTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '4px',
    '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
    '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
    '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue, boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightBlue}` },
  },
  '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium },
  '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium },
}));

export default function ClientSettings() {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const { clientName } = location.state || {};
  const { clientCode } = useClientCode();

  // State
  const [settings, setSettings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<any>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const fetchSettings = async () => {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const settingsData = await fetchClientSettings(clientCode, clientId);
      setSettings(settingsData);
    } catch (err) {
      logger.error('Error fetching client settings:', err);
      toast.error('Failed to fetch client settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [clientId]);

  const handleAddSetting = async () => {
    if (!newKey.trim() || !newValue.trim()) {
      toast.error('Please fill in both key and value');
      return;
    }

    try {
      await addClientSetting(clientCode, {
        key: newKey.trim(),
        value: newValue.trim(),
        clientId: Number(clientId),
      });
      setAddDialogOpen(false);
      setNewKey('');
      setNewValue('');
      fetchSettings();
      toast.success('Setting added successfully');
    } catch (err) {
      logger.error('Error adding setting:', err);
      toast.error('Failed to add setting');
    }
  };

  const handleEditSetting = async () => {
    if (!editingSetting || !newKey.trim() || !newValue.trim()) {
      toast.error('Please fill in both key and value');
      return;
    }

    try {
      await updateClientSetting(clientCode, {
        id: editingSetting.id,
        key: newKey.trim(),
        value: newValue.trim(),
        clientId: Number(clientId),
      });
      setEditDialogOpen(false);
      setEditingSetting(null);
      setNewKey('');
      setNewValue('');
      fetchSettings();
      toast.success('Setting updated successfully');
    } catch (err) {
      logger.error('Error updating setting:', err);
      toast.error('Failed to update setting');
    }
  };

  const handleEditClick = (setting: any) => {
    setEditingSetting(setting);
    setNewKey(setting.key);
    setNewValue(setting.value);
    setEditDialogOpen(true);
  };

  const handleBack = () => {
    navigate('/clients');
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Header with back arrow */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: SAP_SPACING.xl }}>
        <IconButton
          onClick={handleBack}
          sx={{
            marginRight: SAP_SPACING.md,
            backgroundColor: SAP_COLORS.sapGray2,
            color: SAP_COLORS.sapGray7,
            '&:hover': { backgroundColor: SAP_COLORS.sapGray3 },
          }}
          aria-label="Back to Clients"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: SAP_SPACING.sm }}>
            <SettingsIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: '2rem' }} />
            <Box>
              <Typography variant="h4" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
                {clientName || 'Client Settings'}
              </Typography>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5, fontSize: '0.9375rem' }}>
                Manage client settings and configurations
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewKey('');
              setNewValue('');
              setAddDialogOpen(true);
            }}
            sx={{
              backgroundColor: SAP_COLORS.sapGreen,
              color: SAP_COLORS.sapWhite,
              '&:hover': { backgroundColor: '#1b5e20' },
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            }}
          >
            Add Setting
          </Button>
        </Box>
      </Box>


      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} />
        </Box>
      )}

      {/* Settings Table */}
      {!loading && (
        <SAPDataCard>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table sx={{ minWidth: { xs: 300, sm: 600, md: 900 } }} stickyHeader aria-label="client settings table">
                <TableHead>
                  <TableRow>
                    <SAPTableHeader sx={{ width: '10%' }}>S.No.</SAPTableHeader>
                    <SAPTableHeader sx={{ width: '35%' }}>Key</SAPTableHeader>
                    <SAPTableHeader sx={{ width: '45%' }}>Value</SAPTableHeader>
                    <SAPTableHeader sx={{ width: '10%' }}>Action</SAPTableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: SAP_COLORS.sapGray5, py: SAP_SPACING.xl }}>
                        No settings found for this client.
                      </TableCell>
                    </TableRow>
                  ) : (
                    settings.map((setting, idx) => (
                      <TableRow
                        key={setting.id}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                          '&:hover': { backgroundColor: SAP_COLORS.hover },
                        }}
                      >
                        <TableCell sx={{ color: SAP_COLORS.sapGray7, fontSize: '0.875rem' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ color: SAP_COLORS.sapGray7, fontSize: '0.875rem', fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>{setting.key}</TableCell>
                        <TableCell sx={{ color: SAP_COLORS.sapGray6, fontSize: '0.875rem', wordBreak: 'break-word' }}>{setting.value}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleEditClick(setting)} sx={{ color: SAP_COLORS.sapBlue }} aria-label="Edit">
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </SAPDataCard>
      )}

      {/* Add Setting Dialog */}
      {/* Add Setting Dialog - same structure as Create User / Roles modals */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setNewKey('');
          setNewValue('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.lg,
            maxHeight: { xs: '95vh', sm: '90vh' },
            m: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 2.5,
            px: { xs: 2, sm: 3 },
            bgcolor: SAP_COLORS.sapGray1,
            color: SAP_COLORS.sapGray7,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          Add New Setting
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            bgcolor: SAP_COLORS.sapWhite,
            px: { xs: 2, sm: 3 },
            py: 4,
            paddingTop: '32px !important',
            paddingBottom: '32px !important',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
            <SAPTextField label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="Enter key" fullWidth size="small" />
            <SAPTextField label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Enter value" fullWidth multiline rows={3} size="small" />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2.5,
            gap: 1.5,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
            bgcolor: SAP_COLORS.sapWhite,
          }}
        >
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setNewKey('');
              setNewValue('');
            }}
            sx={{ color: SAP_COLORS.sapGray6 }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddSetting} variant="contained" sx={{ backgroundColor: SAP_COLORS.sapGreen, '&:hover': { backgroundColor: '#1b5e20' } }}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Setting Dialog - same structure as Create User / Roles modals */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingSetting(null);
          setNewKey('');
          setNewValue('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.lg,
            maxHeight: { xs: '95vh', sm: '90vh' },
            m: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 2.5,
            px: { xs: 2, sm: 3 },
            bgcolor: SAP_COLORS.sapGray1,
            color: SAP_COLORS.sapGray7,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          Edit Setting
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            bgcolor: SAP_COLORS.sapWhite,
            px: { xs: 2, sm: 3 },
            py: 4,
            paddingTop: '32px !important',
            paddingBottom: '32px !important',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
            <SAPTextField label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="Enter key" fullWidth size="small" />
            <SAPTextField label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Enter value" fullWidth multiline rows={3} size="small" />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2.5,
            gap: 1.5,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
            bgcolor: SAP_COLORS.sapWhite,
          }}
        >
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditingSetting(null);
              setNewKey('');
              setNewValue('');
            }}
            sx={{ color: SAP_COLORS.sapGray6 }}
          >
            Cancel
          </Button>
          <Button onClick={handleEditSetting} variant="contained" sx={{ backgroundColor: SAP_COLORS.sapBlue, '&:hover': { backgroundColor: SAP_COLORS.sapBlueDark } }}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 